import { useDispatch } from 'react-redux';
import {
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { clearHealRunReport } from '../../../redux/locatorDrift/locatorDriftSlice';
import MetricCard from '../shared/MetricCard';
import { healRunReportColors, healRunReportStyles } from './healingStyles';

export default function HealRunReport({ report }) {
  const dispatch = useDispatch();

  return (
    <Paper variant="outlined" sx={healRunReportStyles.root}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', md: 'center' }}
        sx={healRunReportStyles.header}
      >
        <Chip
          size="small"
          color={report.dry_run ? 'info' : 'success'}
          label={report.dry_run ? 'DRY-RUN' : 'APPLIED'}
        />
        <Typography variant="body2">
          <strong>{report.from_version}</strong> -&gt; <strong>{report.to_version}</strong> (
          {report.platform})
        </Typography>
        <Button
          size="small"
          onClick={() => dispatch(clearHealRunReport())}
          sx={healRunReportStyles.clearButton}
        >
          Clear
        </Button>
      </Stack>
      <Grid container spacing={1.5} sx={healRunReportStyles.summaryGrid}>
        <Grid item xs={6} md={2}>
          <MetricCard
            label="High"
            value={report.risks_high || 0}
            color={healRunReportColors.high}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <MetricCard
            label="Medium"
            value={report.risks_medium || 0}
            color={healRunReportColors.medium}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <MetricCard label="Low" value={report.risks_low || 0} color={healRunReportColors.low} />
        </Grid>
        <Grid item xs={6} md={2}>
          <MetricCard
            label="Auto"
            value={report.actions_auto || 0}
            color={healRunReportColors.auto}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <MetricCard
            label="Prompt"
            value={report.actions_prompt || 0}
            color={healRunReportColors.prompt}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <MetricCard
            label="Failed"
            value={report.rewrites_failed || 0}
            color={healRunReportColors.failed}
          />
        </Grid>
      </Grid>
      {Array.isArray(report.actions) && report.actions.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Class</TableCell>
              <TableCell>Attribute</TableCell>
              <TableCell>Change</TableCell>
              <TableCell>Score</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {report.actions.map((action, index) => {
              const oldValue = action.old_tuple?.[1] || '';
              const newValue = action.new_tuple?.[1] || '';
              const status = action.result_ok
                ? report.dry_run
                  ? 'diff'
                  : 'written'
                : action.error || action.classification || 'error';

              return (
                <TableRow key={`${action.file || 'file'}-${index}`}>
                  <TableCell sx={healRunReportStyles.monoCell}>{action.class || ''}</TableCell>
                  <TableCell sx={healRunReportStyles.monoCell}>{action.attribute || ''}</TableCell>
                  <TableCell sx={healRunReportStyles.changeCell}>
                    <Typography
                      variant="caption"
                      sx={healRunReportStyles.oldValue}
                      title={oldValue}
                    >
                      - {oldValue}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={healRunReportStyles.newValue}
                      title={newValue}
                    >
                      + {newValue}
                    </Typography>
                  </TableCell>
                  <TableCell>{(action.score || 0).toFixed(3)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={status} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No actionable locator changes found.
        </Typography>
      )}
    </Paper>
  );
}
